import { AbilityBuilder, Ability } from '@casl/ability'
import { ACTIVITIES } from "@/store/auth/auth";
import store from "../index";
import {CatchingEventAttributes} from "@/pages/AppPages/Events/Catching/catching";
import {RequestStatus} from "@/pages/AppPages/Requests/request";
import {RaidStatus} from "@/pages/AppPages/Raids/raid";

// CASL permissions for rendering inputs and components

const resources = [
    'Dashboard', 'Profile', 'Statistics',
    'Catalogs', 'AnimalCatalog', 'OrganizationCatalog',
    'ContractCatalog', 'MunicipalityCatalog',
    'EventsCatalog', 'BreedCatalog', 'RequestCatalog',
    'EuthanasiaReasonCatalog', 'NewsCatalog', 'FAQCatalog'
];

const models = [
    'Request', 'Raid', 'Contract',
    'Organization', 'Municipality',
    'Animal', 'Species', 'Breed',
    'CatchingEvent', 'ClinicalEvent',
    'KeepingEvent', 'DisposalEvent',
    'EuthanasiaReason', 'News',
    'QuestionGroup', 'Question'
];

function _hasActivity(activities, activity) {
    return activities.indexOf(activity) >= 0
}


function _toggleAllowAction(action, subset, allowCallback, fields = null, conditions = null) {
    for (const i in subset) {
        if (fields && conditions) {
            allowCallback(action, subset[i], fields, conditions)
        } else if (fields) {
            allowCallback(action, subset[i], fields)
        } else if (conditions) {
            allowCallback(action, subset[i], conditions)
        } else {
            allowCallback(action, subset[i]);
        }
    }
}

function _toggleAllowResourcesAction(resourcesSubset, allowCallback, fields = null, conditions = null) {
    _toggleAllowAction('view', resourcesSubset, allowCallback, fields, conditions)
}

function _allowResources(resourcesSubset, can) {
    for (const i in resourcesSubset) {
        can('view', resourcesSubset[i]);
    }
}

function _allowReadingModels(modelsSubset, can) {
    for (const i in modelsSubset) {
        can('read', modelsSubset[i]);
    }
}

function _allowCreatingModels(modelsSubset, can) {
    for (const i in modelsSubset) {
        can('create', modelsSubset[i], can);
    }
}

function _allowUpdatingModels(modelsSubset, can) {
    for (const i in modelsSubset) {
        can('update', modelsSubset[i]);
    }
}

function _allowDestroyingModels(modelsSubset, can) {
    for (const i in modelsSubset) {
        can('destroy', modelsSubset[i]);
    }
}


const getters = {
    ability: (s, getters) => {

        const { can, cannot, build } = new AbilityBuilder(Ability);

        for (const i in resources) {
            cannot('view', resources[i]);
        }
        for (const i in models) {
            cannot('read', models[i])
        }

        if (getters.isAuthenticated) {
            can('view', 'Dashboard');
            can('view', 'Profile');

            can('read', 'Profile');
            can('update', 'Profile');

            const userOrganizationId = getters.userOrganizationId;
            const groups = getters.userGroups;
            const activities = getters.userOrganizationActivities;
            const municipalities = getters.userOrganizationMunicipalities;

            const isInvalidContractor = getters.isInContractor && !getters.userLastValidContract;
            const isValidContractor = getters.isInContractor && getters.userLastValidContract;
            const cannotWorkWithoutMunicipality = municipalities.length === 0 && getters.hasMunicipalityRestrictions;
            const cannotWorkWithoutContract = !getters.isInController && isInvalidContractor;

            if (getters.isSuperuser) {
                _allowResources(resources, can);
                _allowReadingModels(models, can);
                _allowCreatingModels(models, can);
                _allowUpdatingModels(models, can);
                _allowDestroyingModels(models, can);
                return build(); // суперпользователь может все
            }

            if (!groups
                || !Array.isArray(groups)
                || !userOrganizationId
                || (userOrganizationId && (cannotWorkWithoutMunicipality || cannotWorkWithoutContract))
            ) { return build() }

            _allowResources(['AnimalCatalog', 'EventsCatalog',
                'Catalogs', 'ContractCatalog', 'Statistics'], can);

            // костыль для подведомственных ветстанций с функцией контроля
            // если нет активного контракта, они могут просматривать все, но без возможности редактирования
            if (getters.isInContractor && _hasActivity(activities, ACTIVITIES.CONTROL) && isInvalidContractor) {
                _allowResources(resources, can);
                _allowReadingModels(models, can);
                cannot('view', 'FAQCatalog');
                cannot('read', 'QuestionGroup');
                cannot('read', 'Question');

                return build();
            }

            if (getters.isInContractor) {
                _allowResources(['BreedCatalog', 'NewsCatalog', 'OrganizationCatalog'], can);
                _allowReadingModels(['Animal', 'Breed', 'News', 'Organization'], can);

                can('read', 'Contract', {contractor: userOrganizationId});
                can('read', 'Request', {assignedToId: userOrganizationId});
                can('update', 'Request', ['status', 'result', 'refuseReason'], {assignedToId: userOrganizationId});
                can('create', 'Raid');
                can('read', 'Raid', {requestContractorId: userOrganizationId});
                can('update', 'Raid', {requestContractorId: userOrganizationId});
                can('destroy', 'Raid', {authorOrganizationId: userOrganizationId, status: RaidStatus.PLANNED});
                can('create', 'Breed');
                can('update', 'Breed');
                can('destroy', 'Breed');

                cannot('update', 'Request', ['contract']);
                cannot('update', 'CatchingEvent', ['executorContract']);
                cannot('update', 'DisposalEvent', ['executorContract']);
                cannot('update', 'KeepingEvent', ['executorContract']);
                cannot('update', 'ClinicalEvent', ['executorContract']);

                can('read', 'Organization', {id: userOrganizationId});
                can('update', 'Organization', {id: userOrganizationId});

                if (_hasActivity(activities, ACTIVITIES.CATCHING)) {
                    _allowResources(['RequestCatalog'], can);
                    can('create', 'Animal');
                    can('update', 'Animal');
                    can('destroy', 'Animal');
                    can('create', 'CatchingEvent');
                    can('read', 'CatchingEvent');
                    can('update', 'CatchingEvent', {authorOrganizationId: userOrganizationId});
                    can('destroy', 'CatchingEvent', {authorOrganizationId: userOrganizationId});
                }

                if (_hasActivity(activities, ACTIVITIES.RELEASE)) {
                    _allowResources(['RequestCatalog'], can);
                    can('read', 'DisposalEvent');
                    can('create', 'DisposalEvent');
                    can('update', 'DisposalEvent', {authorOrganizationId: userOrganizationId});
                    can('destroy', 'DisposalEvent', {authorOrganizationId: userOrganizationId});
                    can('update', 'DisposalEvent', ['attachments']);
                    can('update', 'Request', ['contract'], {authorOrganizationId: userOrganizationId});
                }

                if (_hasActivity(activities, ACTIVITIES.KEEPING)) {
                    _allowResources(['RequestCatalog'], can);
                    can('create', 'Animal');
                    can('destroy', 'Animal');
                    can('update', 'Animal');
                    can('read', 'CatchingEvent');
                    can('read', 'KeepingEvent');
                    can('create', 'KeepingEvent');
                    can('update', 'KeepingEvent', {authorOrganizationId: userOrganizationId});
                    can('destroy', 'KeepingEvent', {authorOrganizationId: userOrganizationId});
                    can('update', 'KeepingEvent', {executorOrganizationId: userOrganizationId});
                    can('destroy', 'KeepingEvent', {executorOrganizationId: userOrganizationId});
                }

                if (_hasActivity(activities, ACTIVITIES.VET)) {
                    _allowResources(['RequestCatalog'], can);  // веткилиники могут просматривать заявки на выпуск
                    can('create', 'Animal');
                    can('destroy', 'Animal');
                    can('update', 'Animal');
                    can('read', 'ClinicalEvent');
                    can('create', 'ClinicalEvent');
                    can('update', 'ClinicalEvent', {authorOrganizationId: userOrganizationId});
                    can('destroy', 'ClinicalEvent', {authorOrganizationId: userOrganizationId});
                    can('update', 'Request', {activity: ACTIVITIES.RELEASE});  // ветклиники имеют право редактировать заявки на выпуск согласно http://172.21.251.166:8080/browse/PETS-213

                    if (_hasActivity(activities, ACTIVITIES.CONTROL)) {
                        // подведомственные ветстанции могут просматривать все мероприятия
                        can('read', 'CatchingEvent');
                        can('read', 'DisposalEvent');
                        can('read', 'KeepingEvent');
                    }
                }

                can('view', 'News');
                can('create', 'News');
                can('read', 'News', {authorOrganizationId: userOrganizationId});
                can('update', 'News', {authorOrganizationId: userOrganizationId});
                can('destroy', 'News', {authorOrganizationId: userOrganizationId});
            }

            if (getters.isInCustomer) {
                _allowResources(['RequestCatalog'], can);
                can('create', 'Contract');
                can('update', 'Contract', {customer: userOrganizationId});

                can('create', 'Request');
                can('update', 'Request', {authorOrganizationId: userOrganizationId});
                can('update', 'Request', ['contract']);
                if (getters.hasMunicipalityRestrictions) {
                    can('read', 'Request', ({ municipality }) => municipalities.includes(municipality));
                } else {
                    can('read', 'Request');
                }
                can('destroy', 'Request', {authorOrganizationId: userOrganizationId, status: RequestStatus.OPEN});

                if (_hasActivity(activities, ACTIVITIES.CONTROL)) {
                    can('create', 'Breed');
                    can('update', 'Breed');
                    can('destroy', 'Breed');
                    can('create', 'Species');
                    can('update', 'Species');
                    can('destroy', 'Contract', { customer: userOrganizationId })
                }
            }

            cannot('update', 'Animal', ['reviewDate']);
            cannot('update', 'News', ['reviewComment']);

            if (getters.isInController) {
                _allowResources(resources, can);
                _allowReadingModels(models, can);

                _allowCreatingModels(['Organization'], can);
                can('update', 'Organization', {authorOrganizationId: userOrganizationId});
                can('update', 'Organization', {id: userOrganizationId });
                can('destroy', 'Organization', {authorOrganizationId: userOrganizationId});

                if (_hasActivity(activities, ACTIVITIES.CONTROL)) {
                    _toggleAllowAction('create', ['Municipality', 'EuthanasiaReason', 'QuestionGroup', 'Question', 'News'], can)
                    _toggleAllowAction('update', ['Municipality', 'EuthanasiaReason', 'QuestionGroup', 'Question', 'Organization'], can)
                    _toggleAllowAction('destroy', ['Municipality', 'EuthanasiaReason', 'QuestionGroup', 'Question', 'Organization'], can)
                    can('update', 'Animal', ['reviewDate']);
                    can('update', 'News', {authorOrganizationId: userOrganizationId});
                    can('destroy', 'News', {authorOrganizationId: userOrganizationId});
                    can('update', 'News', ['reviewComment', 'status']);
                }

                if (getters.hasMunicipalityRestrictions) {
                    _toggleAllowResourcesAction(['MunicipalityCatalog', 'EuthanasiaReasonCatalog', 'FAQCatalog', 'NewsCatalog'], cannot)
                    _toggleAllowAction('read', ['Municipality', 'EuthanasiaReason', 'News', 'QuestionGroup', 'Question'], cannot)
                    _toggleAllowAction('create', ['Municipality', 'EuthanasiaReason', 'News', 'QuestionGroup', 'Question'], cannot)
                    _toggleAllowAction('update', ['Municipality', 'EuthanasiaReason', 'News', 'QuestionGroup', 'Question', 'Organization'], cannot)
                    _toggleAllowAction('destroy', ['Municipality', 'EuthanasiaReason', 'News', 'QuestionGroup', 'Question', 'Organization'], cannot)
                    cannot('update', 'Animal', ['reviewDate']);

                    can('update', 'Organization', {authorOrganizationId: userOrganizationId});
                    can('update', 'Organization', {id: userOrganizationId });
                    can('destroy', 'Organization', {authorOrganizationId: userOrganizationId});
                }
            }

        }
        return build();
    },

    menu: (s, getters) => {

        const catalogsMenu = [
            {
                name: 'org',
                title: 'Организации',
                available: store.getters.ability.can('view', 'OrganizationCatalog'),
                to: '/organizations',
            },
            {
                to: '/breeds',
                name: 'breeds',
                title: 'Виды и породы',
                available: store.getters.ability.can('view', 'BreedCatalog')
            },
            {
                to: '/contracts',
                name: 'contracts',
                title: 'Контракты',
                available: store.getters.ability.can('view', 'ContractCatalog'),
            },
            {
                to: '/municipalities',
                name: 'municipalities',
                title: 'Муниципалитеты',
                available: store.getters.ability.can('view', 'MunicipalityCatalog'),
            },
            {
                title: 'Информационные материалы',
                name: 'faq',
                children: [
                        {
                            to: '/question-groups',
                            name: 'questionGroups',
                            title: 'Тематики',
                            available: store.getters.ability.can('view', 'FAQCatalog'),
                        },
                        {
                            to: '/questions',
                            name: 'questions',
                            title: 'Информационные материалы',
                            available: store.getters.ability.can('view', 'FAQCatalog'),
                        },
                ],
                available: store.getters.ability.can('view', 'FAQCatalog')
            },
            {
                to: '/euthanasia-reasons',
                name: 'euthanasiaReasons',
                title: 'Основание для эвтаназии',
                available: store.getters.ability.can('view', 'EuthanasiaReasonCatalog'),
            },
        ];

        return [
            {
                to: "/dashboard",
                name: 'main',
                title: 'Главная',
                available: store.getters.ability.can('view', 'Dashboard')
            },
            {
                to: "/requests/",
                name: 'requests',
                title: 'Заявки',
                available: store.getters.ability.can('view', 'RequestCatalog')
            },
            {
                to: "/animals",
                name: 'animals',
                title: 'Животные',
                available: store.getters.ability.can('view', 'AnimalCatalog')
            },
            {
                to: '/events',
                name: 'events',
                title: 'Мероприятия',
                available: store.getters.ability.can('view', 'EventsCatalog'),
            },
            {
                to: '/news',
                name: 'news',
                title: 'Новости',
                available: store.getters.ability.can('view', 'NewsCatalog'),
            },
            {
                to: '/statistics',
                name: 'statistics',
                title: 'Аналитика',
                available: store.getters.ability.can('view', 'Statistics'),
            },
            {
                title: 'Справочники',
                name: 'info',
                children: catalogsMenu,
                available: store.getters.ability.can('view', 'Catalogs')
            }
        ];


    }
};

export default {
    getters,
}
